if (!customElements.get('training-program-quiz')) {
  customElements.define(
    'training-program-quiz',
    class TrainingProgramQuiz extends HTMLElement {
      connectedCallback() {
        if (this.dataset.quizInitialized === 'true') return;
        this.dataset.quizInitialized = 'true';

        this.panels = [...this.querySelectorAll('[data-quiz-panel]')];
        this.stepButtons = [...this.querySelectorAll('[data-quiz-step-button]')];
        this.backButton = this.querySelector('[data-quiz-back]');
        this.restartButton = this.querySelector('[data-quiz-restart]');
        this.progress = this.querySelector('[data-quiz-progress]');
        this.results = this.querySelector('[data-quiz-results]');
        this.copyDiscountButton = this.querySelector('[data-quiz-copy-discount]');
        this.currentStep = 0;
        this.maxReachedStep = 0;
        this.answers = {};
        this.products = this.readProducts();

        this.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.addEventListener('change', () => this.handleAnswer(input));
        });
        this.backButton?.addEventListener('click', () => this.showStep(this.currentStep - 1));
        this.restartButton?.addEventListener('click', () => this.restart());
        this.copyDiscountButton?.addEventListener('click', () => this.copyDiscountCode());
        this.stepButtons.forEach((button) => {
          button.addEventListener('click', () => this.showStep(Number(button.dataset.quizStepButton)));
        });

        this.showStep(0);
      }

      readProducts() {
        const data = this.querySelector('[data-quiz-products]');
        if (!data) return [];

        try {
          return JSON.parse(data.textContent).map((product, index) => ({
            ...product,
            index,
            normalizedTags: product.tags.map((tag) => tag.toLowerCase().trim()),
          }));
        } catch (error) {
          console.warn('Treeniohjelmasuosittelijan tuotetietoja ei voitu lukea.', error);
          return [];
        }
      }

      handleAnswer(input) {
        const answerGroup = input.name.split('-')[0];
        this.answers[answerGroup] = input.value;

        window.setTimeout(() => {
          if (this.currentStep === 4) {
            this.renderResults();
            window.localStorage.setItem('trainingQuizCompleted', String(Date.now()));
            this.showStep(5);
          } else {
            this.showStep(this.currentStep + 1);
          }
        }, 180);
      }

      showStep(step) {
        if (step < 0 || step >= this.panels.length) return;

        this.currentStep = step;
        this.maxReachedStep = Math.max(this.maxReachedStep, step);
        this.panels.forEach((panel, index) => {
          panel.hidden = index !== step;
          panel.classList.toggle('is-active', index === step);
        });

        this.stepButtons.forEach((button, index) => {
          button.classList.toggle('is-active', index === step);
          button.classList.toggle('is-complete', index < step);
          button.disabled = index > this.maxReachedStep;
          button.setAttribute('aria-current', index === step ? 'step' : 'false');
        });

        if (this.progress) {
          this.progress.style.width = `${((step + 1) / this.panels.length) * 100}%`;
        }

        if (this.backButton) this.backButton.hidden = step === 0 || step === 5;
        if (this.restartButton) this.restartButton.hidden = step !== 5;

        const activePanel = this.panels[step];
        const heading = activePanel?.querySelector('legend, h3');
        if (heading && step > 0) heading.focus?.({ preventScroll: true });
      }

      scoreProduct(product) {
        const tags = product.normalizedTags;
        let score = product.available ? 0 : -1;
        const reasons = [];
        const has = (tag) => tags.includes(tag);

        if (has(`quiz_goal_${this.answers.goal}`)) {
          score += 5;
          reasons.push('tavoitteesi');
        }

        const locationTags = tags.filter((tag) => tag.startsWith('quiz_location_'));
        const flexibleLocation =
          has('quiz_location_joustava') ||
          ['quiz_location_kuntosali', 'quiz_location_koti', 'quiz_location_ulkosali'].filter((tag) => has(tag)).length >= 2;
        if (
          has(`quiz_location_${this.answers.location}`) ||
          has('quiz_location_joustava') ||
          (this.answers.location === 'joustava' && flexibleLocation)
        ) {
          score += 4;
          reasons.push('haluamasi treenipaikan');
        }

        if (has(`quiz_level_${this.answers.level}`)) {
          score += 3;
          reasons.push('treenitaustasi');
        }

        if (has(`quiz_days_${this.answers.days}`)) {
          score += 4;
          reasons.push('viikoittaisen treenimääräsi');
        }

        const hasGenderTags = tags.some((tag) => tag.startsWith('quiz_gender_'));
        if (
          this.answers.gender === 'kaikki' ||
          has('quiz_gender_kaikki') ||
          has(`quiz_gender_${this.answers.gender}`) ||
          !hasGenderTags
        ) {
          score += 2;
        } else {
          score -= 20;
        }

        const priorityTag = tags.find((tag) => /^quiz_priority_\d+$/.test(tag));
        if (priorityTag) score += Number(priorityTag.replace('quiz_priority_', '')) / 100;

        return { ...product, score, reasons, locationTags };
      }

      renderResults() {
        if (!this.results) return;

        const count = Number(this.dataset.resultCount || 2);
        const ranked = this.products
          .map((product) => this.scoreProduct(product))
          .sort((a, b) => b.score - a.score || a.index - b.index)
          .slice(0, count);

        this.results.innerHTML = '';
        ranked.forEach((product, index) => {
          const sourceCard = this.querySelector(`[data-quiz-product-card="${product.id}"]`);
          if (!sourceCard) return;

          const card = sourceCard.cloneNode(true);
          const label = card.querySelector('[data-quiz-result-label]');

          if (label) label.textContent = index === 0 ? 'Paras suositus' : `${index + 1}. suositus`;

          this.results.appendChild(card);
        });
      }

      async copyDiscountCode() {
        const code = this.querySelector('[data-quiz-discount-code]')?.textContent.trim();
        const status = this.querySelector('[data-quiz-discount-status]');
        const label = this.copyDiscountButton?.querySelector('.btn-txt');
        if (!code || !this.copyDiscountButton) return;

        try {
          await navigator.clipboard.writeText(code);
        } catch (error) {
          const input = document.createElement('textarea');
          input.value = code;
          input.setAttribute('readonly', '');
          input.style.position = 'fixed';
          input.style.opacity = '0';
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
        }

        const copiedLabel = this.copyDiscountButton.dataset.copiedLabel || 'Koodi kopioitu!';
        const copyLabel = this.copyDiscountButton.dataset.copyLabel || 'Kopioi koodi';
        if (label) label.textContent = copiedLabel;
        if (status) status.textContent = copiedLabel;

        window.setTimeout(() => {
          if (label) label.textContent = copyLabel;
          if (status) status.textContent = '';
        }, 3000);
      }

      restart() {
        this.answers = {};
        this.maxReachedStep = 0;
        this.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = false;
        });
        this.showStep(0);
      }
    }
  );
}
